import { AlertCircle, CheckCircle2, Download, RefreshCw, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import {
  getAiFeedback,
  markAiFeedbackReviewed,
  type AiFeedbackItem,
  type ThumbsFeedback
} from '../lib/api/aiFeedback'
import { downloadAiFeedbackMarkdown } from '../utils/aiFeedbackExport'
import { cn } from '../utils/cn'

type FeedbackFilter = 'All' | ThumbsFeedback
type ReviewedFilter = 'unreviewed' | 'all' | 'reviewed'

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—'
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(value))
  } catch {
    return value
  }
}

const lastUserMessage = (item: AiFeedbackItem) => {
  const savedMessages = item.savedConversation?.messages ?? []
  const targetAssistantIndex = savedMessages.findIndex(
    (message) => message.interactionId === item.id
  )
  for (let index = targetAssistantIndex - 1; index >= 0; index -= 1) {
    if (savedMessages[index].role === 'user') {
      return savedMessages[index].content
    }
  }

  const userMessages = item.messages.filter((message) => message.role === 'user')
  return userMessages[userMessages.length - 1]?.content ?? 'No user message captured.'
}

const systemPrompt = (item: AiFeedbackItem) =>
  item.messages.find((message) => message.role === 'system')?.content

const displayedResponse = (item: AiFeedbackItem) => {
  if (item.displayedResponse?.content) {
    return item.displayedResponse.content
  }

  if (item.channel !== 'mobile-coach') {
    return item.response?.content ?? 'No assistant response captured.'
  }

  return 'No saved user-facing response was found for this interaction.'
}

const FeedbackIcon = ({ feedback }: { feedback: ThumbsFeedback }) => (
  feedback === 'Up'
    ? <ThumbsUp className="h-4 w-4 text-emerald-700" />
    : <ThumbsDown className="h-4 w-4 text-rose-700" />
)

export const AiFeedbackPage = () => {
  const [items, setItems] = useState<AiFeedbackItem[]>([])
  const [feedbackFilter, setFeedbackFilter] = useState<FeedbackFilter>('Down')
  const [reviewedFilter, setReviewedFilter] = useState<ReviewedFilter>('unreviewed')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reviewingId, setReviewingId] = useState<string | null>(null)

  const query = useMemo(() => ({
    feedback: feedbackFilter === 'All' ? undefined : feedbackFilter,
    reviewed: reviewedFilter === 'all' ? undefined : reviewedFilter === 'reviewed',
    limit: 100
  }), [feedbackFilter, reviewedFilter])

  const loadFeedback = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    setError(null)

    try {
      const response = await getAiFeedback(query)
      setItems(response.items)
    } catch (err) {
      console.error('Failed to load AI feedback', err)
      setError('Could not load AI feedback right now.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [query])

  useEffect(() => {
    void loadFeedback()
  }, [loadFeedback])

  const markReviewed = async (item: AiFeedbackItem) => {
    setReviewingId(item.id)
    setError(null)

    try {
      const updated = await markAiFeedbackReviewed(item.id)
      setItems((current) => {
        if (reviewedFilter === 'unreviewed') {
          return current.filter((candidate) => candidate.id !== item.id)
        }

        return current.map((candidate) => candidate.id === item.id ? updated : candidate)
      })
    } catch (err) {
      console.error('Failed to mark AI feedback reviewed', err)
      setError('Could not mark that feedback as reviewed.')
    } finally {
      setReviewingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Support</p>
          <h1 className="text-3xl font-semibold text-foreground">AI feedback</h1>
          <p className="text-sm text-muted-foreground">
            Review coach and support assistant responses that received mobile thumbs feedback.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SegmentedFilter
            label="Feedback"
            value={feedbackFilter}
            options={['Down', 'Up', 'All']}
            onChange={(value) => setFeedbackFilter(value as FeedbackFilter)}
          />
          <SegmentedFilter
            label="Review"
            value={reviewedFilter}
            options={['unreviewed', 'reviewed', 'all']}
            labels={{ unreviewed: 'Open', reviewed: 'Reviewed', all: 'All' }}
            onChange={(value) => setReviewedFilter(value as ReviewedFilter)}
          />
          <Button variant="outline" onClick={() => void loadFeedback(true)} disabled={refreshing}>
            <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </header>

      {error && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertCircle className="mt-0.5 h-5 w-5 text-rose-600" />
            <div>
              <p className="font-semibold text-rose-900">Feedback request failed</p>
              <p className="text-sm text-rose-800">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">Loading AI feedback...</CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={CheckCircle2}
              title="No matching feedback"
              description="The current filters do not have any assistant feedback to review."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <FeedbackCard
              key={item.id}
              item={item}
              markingReviewed={reviewingId === item.id}
              onMarkReviewed={() => void markReviewed(item)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const SegmentedFilter = ({
  label,
  value,
  options,
  labels,
  onChange
}: {
  label: string
  value: string
  options: string[]
  labels?: Record<string, string>
  onChange: (value: string) => void
}) => (
  <div className="flex items-center rounded-lg border border-border bg-card p-1">
    <span className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
    {options.map((option) => (
      <button
        key={option}
        type="button"
        onClick={() => onChange(option)}
        className={cn(
          'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
          value === option
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )}
      >
        {labels?.[option] ?? option}
      </button>
    ))}
  </div>
)

const FeedbackCard = ({
  item,
  markingReviewed,
  onMarkReviewed
}: {
  item: AiFeedbackItem
  markingReviewed: boolean
  onMarkReviewed: () => void
}) => {
  const prompt = systemPrompt(item)
  const reviewed = Boolean(item.feedbackReviewedAt)

  return (
    <article className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold',
                item.feedback === 'Up'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-rose-200 bg-rose-50 text-rose-800'
              )}
            >
              <FeedbackIcon feedback={item.feedback} />
              {item.feedback}
            </span>
            <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground">
              {reviewed ? 'Reviewed' : 'Open'}
            </span>
            <span className="text-xs text-muted-foreground">{formatDateTime(item.feedbackAt)}</span>
          </div>

          <div>
            <p className="text-sm font-semibold text-foreground">User message</p>
            <p className="mt-1 whitespace-pre-wrap rounded-md border border-border bg-muted/25 p-3 text-sm leading-6 text-foreground">
              {lastUserMessage(item)}
            </p>
          </div>
        </div>

        <div className="grid shrink-0 gap-2 text-xs text-muted-foreground sm:grid-cols-2 lg:w-80">
          <Detail label="Mobile user" value={item.userId} />
          <Detail label="Channel" value={item.channel} />
          <Detail label="Role" value={item.agentRole} />
          <Detail label="Model" value={item.model ?? '—'} />
          <Detail label="Subscription" value={`${item.subscriptionTier} / ${item.subscriptionStatus}`} />
          <Detail label="Latency" value={item.durationMs == null ? '—' : `${item.durationMs} ms`} />
          <Detail label="Conversation ID" value={item.savedConversation?.id ?? 'Not found'} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <section>
          <p className="text-sm font-semibold text-foreground">User comment</p>
          <p className="mt-1 min-h-16 whitespace-pre-wrap rounded-md border border-border bg-background p-3 text-sm leading-6 text-foreground">
            {item.feedbackComment || 'No comment left.'}
          </p>
        </section>

        <section>
          <p className="text-sm font-semibold text-foreground">Response shown to user</p>
          <p className="mt-1 min-h-16 whitespace-pre-wrap rounded-md border border-border bg-background p-3 text-sm leading-6 text-foreground">
            {displayedResponse(item)}
          </p>
        </section>
      </div>

      {item.savedConversation ? (
        <details className="mt-3 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
          <summary className="cursor-pointer font-semibold text-foreground">
            Saved conversation
            <span className="ml-2 font-normal text-muted-foreground">
              {item.savedConversation.messages.length} messages
            </span>
          </summary>
          <div className="mt-3 max-h-96 space-y-2 overflow-auto">
            {item.savedConversation.messages.map((message, index) => (
              <div
                key={`${message.timestamp}-${index}`}
                className={cn(
                  'rounded-md border p-3',
                  message.interactionId === item.id
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-border bg-background'
                )}
              >
                <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span className="font-semibold uppercase tracking-wide">{message.role}</span>
                  <span>{formatDateTime(message.timestamp)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-foreground">
                  {message.content}
                </p>
              </div>
            ))}
          </div>
        </details>
      ) : item.channel === 'mobile-coach' ? (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Saved conversation not found. This feedback item only has the raw model interaction.
        </p>
      ) : null}

      <details className="mt-3 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
        <summary className="cursor-pointer font-semibold text-foreground">Raw model interaction</summary>
        <p className="mt-2 text-xs leading-5 text-muted-foreground">
          Debug data captured before server-side validation or user-facing formatting.
        </p>
        <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Raw model response
        </p>
        <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-background p-3 text-xs leading-5 text-foreground">
          {item.response?.content ?? 'No raw model response captured.'}
        </pre>
        {prompt ? (
          <>
            <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              System prompt
            </p>
            <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-background p-3 text-xs leading-5 text-foreground">
              {prompt}
            </pre>
          </>
        ) : null}
      </details>

      <div className="mt-4 flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          {reviewed
            ? `Reviewed ${formatDateTime(item.feedbackReviewedAt)} by ${item.feedbackReviewedByUserId ?? 'unknown'}`
            : `Interaction ${item.id}`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => downloadAiFeedbackMarkdown(item)}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Download conversation
          </Button>
          {!reviewed && (
            <Button size="sm" onClick={onMarkReviewed} disabled={markingReviewed}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {markingReviewed ? 'Saving...' : 'I reviewed this'}
            </Button>
          )}
        </div>
      </div>
    </article>
  )
}

const Detail = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-md border border-border bg-background px-3 py-2">
    <p className="font-semibold uppercase tracking-wide">{label}</p>
    <p className="mt-1 break-words font-mono text-[11px] text-foreground">{value}</p>
  </div>
)
